import { randomlyChoseN } from './Utilities';
import {
  tile_id_to_tile_name,
  tile_name_to_available_objects,
  name_to_attributes,
  bass,
  gar,
  name_to_tile_id,
  yard_grass,
  road,
  graveyard,
} from './dataClasses/Mappings';
import { Landscape } from './Landscape';
import Phaser from 'phaser';
import MainScene from 'src/app/Scenes/MainScene';
import { DropItem } from './DropItem';
import { Creature } from './Creature';
import { GameObjectAttributes } from './CustomAttributes';
import {PlacesList } from './dataClasses/Places';
import { LatLonToXYConverter } from './LatLonToXY';
import { Structure } from './Structure';
import ChunkedScene from './ChunkedScene';
import seedrandom from 'seedrandom';

export class GameObjectManager {
  chunkWidth: number;
  tileWidth: integer;
  tileHeight: integer;
  scene: MainScene;
  bodies: MatterJS.BodiesFactory;

  constructor(scene, chunkWidth, tileWidth, tileHeight) {
    this.scene = scene;
    this.chunkWidth = chunkWidth;
    this.tileWidth = tileWidth;
    this.tileHeight = tileHeight;
    this.bodies = new Phaser.Physics.Matter.MatterPhysics(scene).bodies;
  }

  destroyOldObjects() {
    let count = 0;
    this.scene.children.list.forEach((c) => {

      let rs;
      let removable = true;
      if(c instanceof Landscape){
        rs = <Landscape>c;
      }
      else if(c instanceof DropItem){
        rs = <DropItem>c;
      }
      else if(c instanceof Creature){
        rs = <Creature>c;
      }
      else if(c instanceof Structure){
        rs = <Structure>c;
      }
      else{
        removable = false;
      }
      
      if (removable) {
        if (
          Math.abs(rs.x - this.scene.player.x) >
            ChunkedScene.chunkWidth * ChunkedScene.tileWidth * 1.5 ||
          Math.abs(rs.y - this.scene.player.y) >
            ChunkedScene.chunkHeight * ChunkedScene.tileHeight * 1.5
        ) {
          rs.health = 0;
          rs.destroy();
          this.scene.activeCreatures = this.scene.activeCreatures.filter(
            (ac) => {
              return ac !== undefined;
            }
          );
          count += 1;
        }
      }
    });
  }

  destroyOldObjectsByChunk(chunkX: integer, chunkY: integer) {
    let count = 0;
    let chunkDimsPix = {
      width: ChunkedScene.chunkWidth * ChunkedScene.tileWidth,
      height: ChunkedScene.chunkWidth * ChunkedScene.tileWidth,
    };
    let chunkStart = {
      x: chunkX * chunkDimsPix.width,
      y: chunkY * chunkDimsPix.height,
    };
    this.scene.children.getChildren().forEach((c) => {
      if (
        c.constructor.name == 'Landscape' ||
        c.constructor.name == 'DropItem' ||
        c.constructor.name == 'Creature' ||
        c.constructor.name == 'House'
      ) {
        let rs;
        switch (c.constructor.name) {
          case 'Landscape':
            rs = <Landscape>c;
          case 'DropItem':
            rs = <DropItem>c;
          case 'Creature':
            rs = <Creature>c;
          case 'House':
            rs = <Structure>c
        }

        if (
          rs.x >= chunkStart.x &&
          rs.x <= chunkStart.x + chunkDimsPix.width &&
          rs.y >= chunkStart.y &&
          rs.y <= chunkStart.y + chunkDimsPix.height
        ) {
          rs.destroy();
          count += 1;
        }
      }
    });
  }

  generateObjects(map: Phaser.Tilemaps.Tilemap, chunkX: number, chunkY: number) {
    let count = 0;
    const flattenedTileIndices = this.getBaseTileValues(map) as Array<number>;

    // Create a deterministic seed for this chunk
    const rng = seedrandom(`${chunkX},${chunkY}`);

    // Add in houses and mausoleums.
    this.placeStructure(chunkX, chunkY, flattenedTileIndices, 5, 5, yard_grass, 'house', 'House 1');
    this.placeStructure(chunkX, chunkY, flattenedTileIndices, 5, 5, graveyard, 'mausoleum', 'Mausoleum 1');

    tile_id_to_tile_name.forEach((tile_name, tile_id) => {
      // Get indices of tiles that match the tile id
      var targetIndices = this.getTargetIndices(flattenedTileIndices, tile_id);

      // Verify that the tile name has associated RotationObjects
      tile_name_to_available_objects.get(tile_name).forEach((object_name) => {
        // Of the applicable tiles,
        // randomly sub-sample to select for object placement
        var selectedTiles = randomlyChoseN(targetIndices, 1, rng);
        selectedTiles.forEach((tileIdx) => {
          var { x, y } = this.getGlobalTileXY(chunkX, chunkY, tileIdx);

          let attributes: GameObjectAttributes =
            name_to_attributes.get(object_name);

          // Add landscaping (trees, rocks, graves)
          if (attributes.type == 'tree' || attributes.type == 'stone') {
            new Landscape(
              {
                scene: this.scene,
                x: x * this.tileWidth,
                y: y * this.tileHeight,
                texture: 'landscapes',
                frame: object_name,
              },
              this.bodies,
              attributes.health
            );
          }

          // Add roaming creatures
          else if (attributes.type == 'creature') {
            let creature = new Creature(
              {
                scene: this.scene,
                x: x * this.tileWidth,
                y: y * this.tileHeight,
                texture: object_name,
              },
              this.bodies,
              attributes.aggressive
            );
            if(object_name === bass || object_name === gar){
              creature.setAlpha(0.5);
            }
            this.scene.activeCreatures.push(creature);
          }
          count++;
        });
      });
    });
  }

  getBaseTileValues(map: Phaser.Tilemaps.Tilemap, concat=true): Array<number> | Array<Array<number>> {
    const baseLayerData = map.layers.find((layer) => layer.name == 'base').data;
    if(concat){
      return [].concat(...baseLayerData).map((tile: Phaser.Tilemaps.Tile) => tile.index);
    }
    return baseLayerData.map((row) => {
      return row.map((tile: Phaser.Tilemaps.Tile) => tile.index);
    });
  }

  getTargetIndices(indices: number[], targetValue: number) {
    var targetIndices = indices.reduce(
      (a, c, i /*Current index*/) => {
        if (c == targetValue) a.push(i); //Add the found index.
        return a;
      },
      [] /*Accumulator to store the found indexes.*/
    );
    return targetIndices;
  }

  reshapeArray(arr: number[], numCols: number) {
    const result: Array<Array<number>> = [];
  
    for (let i = 0; i < arr.length; i += numCols) {
      result.push(arr.slice(i, i + numCols));
    }
  
    return result;
  }

  convolve2D(input: Array<Array<number>>, kernel: Array<Array<number>>) {
    const rows = input.length;
    const cols = input[0].length;
    const kernelRows = kernel.length;
    const kernelCols = kernel[0].length;
    const output: Array<Array<number>> = [];
  
    // Iterate through the input matrix
    for (let y = 0; y < rows - kernelRows + 1; y++) {
      output[y] = [];
      for (let x = 0; x < cols - kernelCols + 1; x++) {
        let sum = 0;
  
        // Perform convolution for each pixel
        for (let ky = 0; ky < kernelRows; ky++) {
          for (let kx = 0; kx < kernelCols; kx++) {
            sum += input[y + ky][x + kx] * kernel[ky][kx];
          }
        }
  
        output[y][x] = sum;
      }
    }
    return output;
  }

  zeroPad2DArray(array: number[][], paddingX: number, paddingY: number) {
    // Create a new array with padded dimensions
    const paddedArray = Array.from({ length: array.length + paddingY * 2 }, () =>
      Array(array[0].length + paddingX * 2).fill(0)
    );
  
    // Copy the original array into the padded array
    for (let i = 0; i < array.length; i++) {
      for (let j = 0; j < array[0].length; j++) {
        paddedArray[i + paddingY][j + paddingX] = array[i][j];
      }
    }
  
    return paddedArray;
  }
  

  shift2DArrayUp(input: number[][], amount: number){
    const shifted = input.slice(amount, -1);
    const numCols = input[0].length;

    // Replace removed rows wth zeros to keep original shape
    for(let i=0; i<=amount; i++){
      shifted.push(Array(numCols).fill(0))
    }

    return shifted;
  }

  placeStructure(chunkX: number, chunkY: number, tileIndices: number[], width: number, height: number, targetTile: string, externalImage: string, internalSceneName: string){
    this.chunkWidth;
    const chunkHeight = tileIndices.length/this.chunkWidth;

    // Select tiles that are being targeted.
    const targetTiles = tileIndices.map((t) => { return t === name_to_tile_id.get(targetTile) ? 1 : 0});
    let target2d = this.reshapeArray(targetTiles, chunkHeight);

    // Select tiles that are road and set them to 1
    const roadTiles = tileIndices.map((t) => { return t === name_to_tile_id.get(road) ? 1 : 0});

    // Count the number of road tiles to make sure houses are near roads.
    const roadSum = roadTiles.reduce((total, next) => { return total + next}, 0)

    const kernel: number[][] = Array.from({ length: height }, () => Array(width).fill(1));

    const convolved = this.convolve2D(target2d, kernel);

    const potentialHouses = convolved.map((row) => {
       return row.map((col) => {
        return col === width*height ? 1 : 0;
       });
    });

    const paddedHouses = this.zeroPad2DArray(potentialHouses, Math.floor(width/2), Math.floor(height/2));

    let validIndices: Set<{rowIdx: number, colIdx: number}> = new Set();
    paddedHouses.map((row, rowIdx) => {
      row.map((col, colIdx) => {
        if(col > 0){
          validIndices.add({rowIdx: rowIdx, colIdx: colIdx});
        }
      });
    });

    if(validIndices.size > 0 && roadSum >=10){
        // Create a deterministic seed for this chunk
        const rng = seedrandom(`${chunkX},${chunkY}`);
        const selectedHouse = [...validIndices][Math.floor(rng() * validIndices.size)];
        const houseXY = this.getGlobalTileXY(chunkX, chunkY, (selectedHouse.rowIdx*this.chunkWidth + selectedHouse.colIdx));
        
        // Convert tile coords to pixel coords
        houseXY.x *= this.tileWidth;
        houseXY.y *= this.tileHeight;

        // Make sure there are no landmarks nearby that may overlap
        for(let i=0; i< PlacesList.length; i++){
          const placeXY = LatLonToXYConverter.latlngToScreenXY(PlacesList[i].coords.latitude, PlacesList[i].coords.longitude);
          if(Math.abs(placeXY.x - houseXY.x) < this.chunkWidth*this.tileWidth/2 || Math.abs(placeXY.y - houseXY.y) < this.chunkWidth*this.tileWidth/2){
            // There is a landmark nearby so exit this function without adding a house.
            return
          }
        }

        // Place the structure at this position.
        new Structure(this.scene, houseXY.x, houseXY.y, externalImage, internalSceneName);
    }
  }

  getTileChunkXY(tileIdx: number) {
    return {
      x: tileIdx % this.chunkWidth,
      y: Math.floor(tileIdx / this.chunkWidth),
    };
  }

  getGlobalTileXY(chunkX: number, chunkY: number, tileIdx: number) {
    const { x, y } = this.getTileChunkXY(tileIdx);
    return {
      x: chunkX + x,
      y: chunkY + y,
    };
  }
}
